import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Banner,
  InlineStack,
  Text,
  Card,
  Button,
  Spinner,
  EmptyState,
} from '@shopify/polaris';
import { WelcomeStep } from '../components/OnboardingWizard/WelcomeStep';
import { WhyAiStep } from '../components/OnboardingWizard/WhyAiStep';
import { ConsentStep } from '../components/OnboardingWizard/ConsentStep';
import { ScanSetupStep } from '../components/OnboardingWizard/ScanSetupStep';
import { SegmentedStepIndicator, PageEntrance } from '../components/ui';
import { useOnboarding } from '../hooks/useOnboarding';
import { useStoreSettings } from '../hooks/useStoreSettings';
import { useTierConfig } from '../providers/TierConfigProvider';
import './OnboardingPage.css';
import { triggerScan, ApiError } from '../services/api';
import {
  trackOnboardingStarted,
  trackOnboardingStepViewed,
  trackOnboardingCompleted,
  trackScanStarted,
  trackEmailNotifyOptedIn,
} from '../services/analytics';
import type { OnboardingStep } from '../types';

/**
 * T023: OnboardingPage — 4-step wizard with Polaris step indicator.
 * Steps: 1=Welcome, 2=Why AI, 3=Consent, 4=Scan Setup.
 * State-driven content switching. Navigates to /scan-progress/:jobId on scan trigger.
 */

const STEP_LABELS = ['Welcome', 'Why AI', 'Consent', 'Scan Setup'] as const;

export function OnboardingPage() {
  const navigate = useNavigate();
  const { onboarding, isLoading, isError, error, updateStepAsync, isUpdating } =
    useOnboarding();
  const { settings: storeSettings } = useStoreSettings();
  const { getLimits } = useTierConfig();
  const freeProductLimit = storeSettings?.productLimit ?? getLimits('FREE')?.productLimit ?? 0;

  // Local wizard state
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1);
  const [consentGiven, setConsentGiven] = useState(false);
  const [emailNotify, setEmailNotify] = useState(false);
  const [isTriggeringScan, setIsTriggeringScan] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  // Sync server step on load (via useEffect to avoid state-in-render)
  const serverStep = onboarding?.step ?? 0;
  useEffect(() => {
    if (serverStep > 0 && serverStep > currentStep && !isLoading) {
      setCurrentStep(serverStep);
    }
  }, [serverStep, currentStep, isLoading]);

  const goToStep = useCallback(
    async (step: OnboardingStep) => {
      try {
        const request: { step: OnboardingStep; consentGiven?: boolean; emailNotifyEnabled?: boolean } = { step };
        if (step === 4 && consentGiven) {
          request.consentGiven = true;
        }
        await updateStepAsync(request);
        setCurrentStep(step);
        setScanError(null);
        trackOnboardingStepViewed(step);
        if (step === 1) trackOnboardingStarted();
      } catch {
        // Step update failed — still allow local navigation
        setCurrentStep(step);
      }
    },
    [updateStepAsync, consentGiven],
  );

  const handleNext = useCallback(() => {
    const nextStep = Math.min(currentStep + 1, 4) as OnboardingStep;
    goToStep(nextStep);
  }, [currentStep, goToStep]);

  const handleBack = useCallback(() => {
    const prevStep = Math.max(currentStep - 1, 1) as OnboardingStep;
    goToStep(prevStep);
  }, [currentStep, goToStep]);

  const handleBeginScan = useCallback(async () => {
    setIsTriggeringScan(true);
    setScanError(null);
    try {
      if (emailNotify) trackEmailNotifyOptedIn();
      trackScanStarted('SYNC', 'FREE');
      const result = await triggerScan({
        scope: 'SYNC',
        emailNotify,
        fromOnboarding: true,
      });
      trackOnboardingCompleted();
      navigate(`/scan-progress/${result.jobId}`, { state: { fromOnboarding: true } });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start scan';
      setScanError(message);
    } finally {
      setIsTriggeringScan(false);
    }
  }, [emailNotify, navigate]);

  // If onboarding is done, redirect to dashboard
  if (onboarding?.done) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <Page title="Welcome to Zelium AI">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" gap="200">
                <Spinner size="small" />
                <Text as="p" variant="bodyMd">
                  Loading…
                </Text>
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // T051: Re-authentication screen for invalid Shopify session/permissions (FR-031)
  if (isError) {
    const isAuthError =
      error instanceof ApiError && (error.status === 401 || error.status === 403);

    if (isAuthError) {
      return (
        <Page title="Zelium AI">
          <Layout>
            <Layout.Section>
              <EmptyState
                heading="Session expired"
                image=""
              >
                <p>
                  Your Shopify session has expired or the app permissions need to be
                  updated. Please reconnect to continue.
                </p>
                <Button
                  variant="primary"
                  onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const shopify = (globalThis as any).shopify;
                    if (typeof shopify?.redirectToAuth === 'function') {
                      shopify.redirectToAuth();
                    } else {
                      globalThis.location.reload();
                    }
                  }}
                >
                  Reconnect
                </Button>
              </EmptyState>
            </Layout.Section>
          </Layout>
        </Page>
      );
    }

    return (
      <Page title="Zelium AI">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load: {error?.message ?? 'Unknown error'}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Nav actions rendered at the top-right, aligned with the page heading.
  const primaryAction =
    currentStep === 4
      ? {
          content: 'Begin scan',
          onAction: handleBeginScan,
          loading: isTriggeringScan,
          disabled: isTriggeringScan,
        }
      : {
          content: 'Next',
          onAction: handleNext,
          loading: isUpdating,
          disabled: isUpdating || (currentStep === 3 && !consentGiven),
        };
  const secondaryActions =
    currentStep > 1
      ? [{ content: 'Back', onAction: handleBack, disabled: isUpdating || isTriggeringScan }]
      : undefined;

  return (
    <PageEntrance>
    <Page title="Welcome to Zelium AI" primaryAction={primaryAction} secondaryActions={secondaryActions}>
      <Layout>
        {/* Step indicator */}
        <Layout.Section>
          <SegmentedStepIndicator
            totalSteps={STEP_LABELS.length}
            currentStep={currentStep - 1}
            labels={[...STEP_LABELS]}
          />
        </Layout.Section>

        {/* Scan error banner */}
        {scanError && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setScanError(null)}>
              <p>{scanError}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Step content */}
        <Layout.Section>
          {currentStep === 1 && <WelcomeStep onNext={handleNext} />}
          {currentStep === 2 && <WhyAiStep onNext={handleNext} onBack={handleBack} />}
          {currentStep === 3 && (
            <ConsentStep
              consentGiven={consentGiven}
              onConsentChange={setConsentGiven}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}
          {currentStep === 4 && (
            <ScanSetupStep
              emailNotify={emailNotify}
              onEmailNotifyChange={setEmailNotify}
              onBeginScan={handleBeginScan}
              onBack={handleBack}
              isLoading={isTriggeringScan}
              productLimit={freeProductLimit}
            />
          )}
        </Layout.Section>
      </Layout>
    </Page>
    </PageEntrance>
  );
}
